import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Trophy, Star, StarOff, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", exam: "JEE", rank: "", year: new Date().getFullYear() - 1, photo_url: "", college: "", quote: "", is_featured: true, order: 0 };

const TopperForm = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (open) setF({ ...EMPTY, ...initial }); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/toppers/photo-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, photo_url: data.photo_url }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f, year: Number(f.year), order: Number(f.order || 0), college: f.college || null, quote: f.quote || null };
      if (initial?.id) await api.put(`/admin/toppers/${initial.id}`, body); else await api.post("/admin/toppers", body);
      toast.success(initial?.id ? "Topper updated" : "Topper added");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="topper-form-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? "Edit ranker" : "Add ranker"}</DialogTitle>
          <DialogDescription>Rankers appear on the public home page. Upload a photo from your phone or paste any public URL.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Full name</Label><Input required value={f.name} onChange={set("name")} data-testid="admin-topper-name-input" /></div>
            <div className="space-y-1.5"><Label>Exam</Label><Input required value={f.exam} onChange={set("exam")} placeholder="JEE, NEET, UPSC…" data-testid="admin-topper-exam-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Rank / Score</Label><Input required value={f.rank} onChange={set("rank")} placeholder="AIR 42 or 99.6 %ile" data-testid="admin-topper-rank-input" /></div>
            <div className="space-y-1.5"><Label>Year</Label><Input required type="number" min={2000} max={2100} value={f.year} onChange={set("year")} data-testid="admin-topper-year-input" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Photo</Label>
            <div className="flex flex-wrap gap-2 items-center">
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="admin-topper-upload-button" className="hover:bg-slate-900 hover:text-white">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                {uploading ? "Uploading…" : "Upload from device"}
              </Button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" capture="environment" onChange={onFile} className="hidden" data-testid="admin-topper-photo-file" />
              <span className="text-xs text-slate-400">or paste a public URL below</span>
            </div>
            <Input required value={f.photo_url} onChange={set("photo_url")} placeholder="https://…/photo.jpg  or  /api/toppers/photos/…" data-testid="admin-topper-photo-input" />
            {f.photo_url && (
              <img src={f.photo_url} alt="preview" className="mt-2 h-32 w-32 object-cover rounded-lg border border-slate-200"
                onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
            )}
          </div>
          <div className="space-y-1.5"><Label>College / Posting (optional)</Label><Input value={f.college} onChange={set("college")} placeholder="IIT Bombay / AIIMS Delhi…" data-testid="admin-topper-college-input" /></div>
          <div className="space-y-1.5"><Label>Quote (optional)</Label><Textarea rows={3} value={f.quote} onChange={set("quote")} maxLength={400} placeholder="One line about how the mock tests helped." data-testid="admin-topper-quote-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Display order (lower = earlier)</Label><Input type="number" value={f.order} onChange={set("order")} data-testid="admin-topper-order-input" /></div>
            <div className="flex items-end gap-3 border border-slate-200 rounded-lg px-4 py-3">
              <div className="flex-1"><div className="text-sm font-medium">Featured on home</div><div className="text-xs text-slate-500">Visible to visitors</div></div>
              <Switch checked={f.is_featured} onCheckedChange={(v) => setF({ ...f, is_featured: v })} data-testid="admin-topper-featured-switch" />
            </div>
          </div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="admin-save-topper-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">Save ranker</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminToppers() {
  const [toppers, setToppers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/toppers").then((r) => setToppers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    try { await api.delete(`/admin/toppers/${t.id}`); toast.success("Ranker removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleFeature = async (t) => {
    try { await api.put(`/admin/toppers/${t.id}`, { ...t, is_featured: !t.is_featured, college: t.college || null, quote: t.quote || null }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="small-label">Control Panel</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Rankers</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">Students shown on the public home page. Add real last-year toppers who used Pinslib — paste the photo URL from Google and the name + rank.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-add-topper-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
          <Plus className="h-4 w-4 mr-1.5" />Add ranker
        </Button>
      </div>

      {toppers.length === 0 ? (
        <div className="mt-10 border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500">No rankers yet. Add your first one above.</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {toppers.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col" data-testid={`admin-topper-card-${t.id}`}>
              <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
                <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(t.name)}&backgroundColor=0f172a&textColor=ffffff`; }} />
                {!t.is_featured && <Badge className="absolute top-3 left-3 bg-slate-200 text-slate-700 hover:bg-slate-200">Hidden</Badge>}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-heading font-semibold">{t.name}</div>
                  <Badge variant="outline" className="font-mono text-[10px] uppercase">{t.exam} · {t.year}</Badge>
                </div>
                <div className="font-mono text-sm text-amber-700 mt-1 inline-flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" />{t.rank}</div>
                {t.college && <div className="text-xs text-slate-500 mt-2">{t.college}</div>}
                {t.quote && <p className="text-xs text-slate-500 mt-2 italic line-clamp-2">"{t.quote}"</p>}
                <div className="flex items-center justify-end gap-1 mt-auto pt-3">
                  <Button size="icon" variant="ghost" onClick={() => toggleFeature(t)} title={t.is_featured ? "Hide from home" : "Feature on home"} data-testid={`admin-topper-feature-${t.id}`}>
                    {t.is_featured ? <Star className="h-4 w-4 text-amber-500 fill-amber-400" /> : <StarOff className="h-4 w-4 text-slate-400" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`admin-edit-topper-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete testId={`admin-delete-topper-${t.id}`} title={`Remove ${t.name}?`} description="This hides them from the home page immediately."
                    onConfirm={() => remove(t)}
                    trigger={<Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-topper-${t.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <TopperForm open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}
